import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, Film, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Clips() {
  const [clips, setClips] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/clips`)
      .then((r) => setClips(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="clips" className="relative py-24 md:py-32" data-testid="clips-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="section-label" data-testid="clips-label">Clips</div>
            <h2 className="font-display font-black uppercase mt-3 text-4xl md:text-5xl leading-tight" data-testid="clips-title">
              Receipts on <span style={{ color: "var(--rv-red)" }}>tape</span>.
            </h2>
            <p className="text-white/65 mt-4 text-base md:text-lg leading-relaxed">
              Real gameplay from the lobby. Same values you can grab above.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 flex items-center gap-2">
            <Film size={14} /> {clips.length} clip{clips.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="mt-12">
          {loading ? (
            <div className="text-white/40 text-sm" data-testid="clips-loading">Loading clips…</div>
          ) : clips.length === 0 ? (
            <div
              className="rv-card rounded-xl p-12 text-center"
              data-testid="clips-empty"
            >
              <Film size={36} className="mx-auto opacity-40" />
              <div className="mt-4 text-white/70 font-display uppercase tracking-widest text-sm">
                Clips dropping soon
              </div>
              <p className="text-white/45 text-sm mt-2 max-w-md mx-auto">
                Highlight reels are being uploaded. Hop in the Discord for the latest plays.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {clips.map((c) => (
                <button
                  key={c.id}
                  className="clip-card group"
                  onClick={() => setActive(c)}
                  data-testid={`clip-card-${c.id}`}
                >
                  <video
                    src={c.video_url}
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="clip-overlay">
                    <div className="h-14 w-14 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-white/20 group-hover:bg-[var(--rv-red)] transition">
                      <Play size={22} className="text-white ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 text-left">
                    <div className="text-white font-display uppercase tracking-tight text-sm truncate">{c.title}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="clip-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="max-w-4xl w-full bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/70 border border-white/15 flex items-center justify-center hover:bg-[var(--rv-red)] transition"
                data-testid="clip-modal-close"
              >
                <X size={18} />
              </button>
              <video
                src={active.video_url}
                controls
                autoPlay
                className="w-full aspect-video bg-black"
              />
              <div className="p-5">
                <div className="font-display uppercase font-bold">{active.title}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
